import { useParams, Link, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useUnifiedCourse, useUnifiedCourses } from '@/lib/hooks/use-unified-courses'
import { useProfile } from '@/lib/hooks/use-profile'
import { usePrefetchLesson } from '@/lib/hooks/use-prefetch'
import { useRole } from '@/hooks/use-role'
import { useFreelancerProfile } from '@/lib/hooks/use-freelancer-profile'
import { usePortfolioProjects } from '@/lib/hooks/use-portfolio-projects'
import { useWorkExperience } from '@/lib/hooks/use-work-experience'
import { useProfileCompletion } from '@/lib/hooks/use-profile-completion'
import { AnimatedLoader } from '@/components/ui/animated-loader'
import {
  BookOpen,
  ArrowLeft,
  PlayCircle,
  CheckCircle2,
  Clock,
  FileText,
  HelpCircle,
  Lock,
  GraduationCap,
  Award,
  Target
} from 'lucide-react'
import { SlideIn, StaggerContainer, StaggerItem } from '@/components/motion'
import { emailService } from '@/lib/api/services/email.service'

export default function CourseDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { user } = useUser()
  const { profile } = useProfile()
  const { freelancerProfileId } = useRole()
  const { course, isLoading, error, enroll, isEnrolling } = useUnifiedCourse(slug!)
  const { prefetchLesson } = usePrefetchLesson()
  
  // Get profile completion data for email sending
  const { freelancerProfile } = useFreelancerProfile(freelancerProfileId, { enableMutations: false })
  const { courses } = useUnifiedCourses()
  const { projects: portfolioProjects } = usePortfolioProjects(freelancerProfileId || '')
  const { experiences: workExperiences } = useWorkExperience(freelancerProfileId || '')
  
  // Calculate course progress
  const enrolledCourses = courses?.filter((c) => c.enrollment) || []
  const completedCourses = enrolledCourses.filter(
    (c) => c.enrollment?.status === "completed"
  ).length
  const totalCourses = courses?.length || 0
  
  const courseProgress = {
    totalCourses,
    completedCourses,
    allCompleted: completedCourses >= totalCourses && totalCourses > 0,
  }
  
  // Calculate profile completion
  const profileCompletion = useProfileCompletion(
    freelancerProfile,
    courseProgress,
    portfolioProjects?.length || 0,
    workExperiences?.length || 0
  )
  
  // Send email when profile reaches 100% completion (only once)
  useEffect(() => {
    if (!user?.id || !profileCompletion.isComplete || !freelancerProfile?.email) return
    
    const storageKey = `genius-profile-completion-email-sent-${user.id}`
    const hasSentEmail = localStorage.getItem(storageKey) === 'true'
    
    if (!hasSentEmail) {
      // Mark as sent immediately to prevent duplicate sends
      localStorage.setItem(storageKey, 'true')
      
      // Send completion email (non-blocking)
      emailService.sendGeniusProfileCompletedEmail(freelancerProfile.email).catch((error) => {
        console.error('Failed to send profile completion email:', error)
        // Remove the flag on error so we can retry
        localStorage.removeItem(storageKey)
      })
    }
  }, [profileCompletion.isComplete, freelancerProfile?.email, user?.id])

  // Only show courses page to geniuses
  if (!profile?.is_genius) {
    return <Navigate to="/genius/courses" replace />
  }

  // Show loading only if we have no cached data
  if (isLoading && !course) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <AnimatedLoader message="Loading course..." />
      </div>
    )
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-8 py-16">
          <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
            <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Course Not Found</h1>
            <p className="text-slate-500 mb-6">
              The course you're looking for doesn't exist or isn't available.
            </p>
            <Link
              to="/genius/courses"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-medium text-base"
              style={{
                background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Courses
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const isEnrolled = !!course.enrollment
  const isCompleted = course.enrollment?.status === 'completed'
  const progressPercent = course.enrollment?.progress_percent || 0

  // Calculate lesson stats
  const totalLessons = course.lessons.length
  const completedLessons = course.lessons.filter(
    l => l.progress?.completed_at !== null && l.progress?.completed_at !== undefined
  ).length

  // Calculate total duration
  const totalDurationSeconds = course.lessons.reduce((acc, l) => acc + (l.duration_seconds || 0), 0)
  const totalDurationMinutes = Math.ceil(totalDurationSeconds / 60)
  const watchedDurationSeconds = course.lessons.reduce((acc, l) => {
    if (l.progress?.completed_at) return acc + (l.duration_seconds || 0)
    if (l.progress?.percent_watched && l.duration_seconds) {
      return acc + Math.round((l.progress.percent_watched / 100) * l.duration_seconds)
    }
    return acc
  }, 0)
  const remainingMinutes = Math.ceil((totalDurationSeconds - watchedDurationSeconds) / 60)

  const getLessonIcon = (lessonType: string) => {
    switch (lessonType) {
      case 'video':
        return <PlayCircle className="h-5 w-5" />
      case 'reading':
        return <FileText className="h-5 w-5" />
      case 'quiz':
        return <HelpCircle className="h-5 w-5" />
      default:
        return <BookOpen className="h-5 w-5" />
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Hero Banner */}
      <div className="max-w-[1400px] mx-auto px-8 pt-8">
        <SlideIn direction="down">
          <div
            className="rounded-[32px] p-10 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)',
            }}
          >
          {/* Decorative circles */}
          <div
            className="absolute -right-[80px] -top-[80px] w-[300px] h-[300px] rounded-full"
            style={{
              background: 'radial-gradient(closest-side, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 100%)',
            }}
          />
          <div
            className="absolute right-[100px] top-[20px] w-[150px] h-[150px] rounded-full"
            style={{
              background: 'radial-gradient(closest-side, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
            }}
          />

          <div className="relative z-10">
            {/* Back Link */}
            <Link
              to="/genius/courses"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Courses</span>
            </Link>

            <h1 className="text-4xl font-bold text-white mb-3">
              {course.title}
            </h1>
            {course.description && (
              <p className="text-lg text-slate-300 max-w-[600px]">
                {course.description}
              </p>
            )}

            {/* Stats Row */}
            <div className="flex gap-8 mt-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
                  <PlayCircle className="w-5 h-5 text-slate-300" />
                </div>
                <div>
                  <div className="text-xl font-bold text-white">{totalLessons}</div>
                  <div className="text-sm text-slate-400">Lessons</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-slate-300" />
                </div>
                <div>
                  <div className="text-xl font-bold text-white">{totalDurationMinutes} min</div>
                  <div className="text-sm text-slate-400">Duration</div>
                </div>
              </div>
              {isEnrolled && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
                    <Target className="w-5 h-5 text-slate-300" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-white">{progressPercent}%</div>
                    <div className="text-sm text-slate-400">Complete</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </SlideIn>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-8 mt-6">
        <div className="flex gap-8">
          {/* Course Content */}
          <div className="flex-1 space-y-6">
            {/* Course Card with Lessons */}
            <SlideIn direction="up" delay={0.2}>
              <div
                className={`rounded-[40px] p-8 ${
                  isEnrolled && !isCompleted
                    ? 'border-2 border-[#4669ed] bg-[rgba(70,105,237,0.05)]'
                    : 'border border-slate-200 bg-white'
                }`}
              >
              {/* Course Header */}
              <div className="flex items-center gap-5 mb-6">
                <div
                  className={`w-16 h-16 rounded-[32px] flex items-center justify-center ${
                    isCompleted
                      ? 'bg-emerald-100'
                      : 'bg-[rgba(70,105,237,0.10)]'
                  }`}
                >
                  {isCompleted ? (
                    <Award className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <GraduationCap className="w-6 h-6 text-[#4669ed]" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-normal text-slate-900 tracking-tight">{course.title}</h2>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <PlayCircle className="w-5 h-5" />
                      {totalLessons} {totalLessons === 1 ? 'lesson' : 'lessons'}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <Clock className="w-5 h-5" />
                      {totalDurationMinutes} min
                    </span>
                  </div>
                </div>
                <div
                  className={`px-4 py-2 rounded-full flex items-center gap-1.5 text-sm ${
                    isCompleted
                      ? 'bg-emerald-100 text-emerald-700'
                      : isEnrolled
                        ? 'bg-[rgba(70,105,237,0.10)] text-[#4669ed]'
                        : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {isCompleted ? (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Completed
                    </>
                  ) : isEnrolled ? (
                    <>
                      <PlayCircle className="w-5 h-5" />
                      In Progress
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Not Enrolled
                    </>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              {isEnrolled && (
                <div className="mb-2">
                  <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        isCompleted ? 'bg-emerald-500' : 'bg-[#4669ed]'
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    {completedLessons} of {totalLessons} lessons completed
                  </div>
                </div>
              )}

              {/* Lessons List */}
              <div className="border-t-2 border-slate-200 pt-6 mt-6 space-y-2">
                {course.sections.length > 0 ? (
                  // Render sections with lessons
                  course.sections.map((section, sectionIndex) => (
                    <div key={section.id} className="mb-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <span className="w-8 h-8 rounded-full bg-[rgba(70,105,237,0.10)] text-[#4669ed] flex items-center justify-center text-sm font-bold">
                          {sectionIndex + 1}
                        </span>
                        {section.title}
                      </h3>
                      <div className="space-y-2">
                        {section.lessons.map((lesson) => {
                          const lessonCompleted = lesson.progress?.completed_at !== null && lesson.progress?.completed_at !== undefined
                          const lessonProgress = lesson.progress?.percent_watched || 0

                          return (
                            <Link
                              key={lesson.id}
                              to={isEnrolled ? `/genius/lesson/${lesson.id}` : '#'}
                              className={`flex items-center gap-4 p-4 rounded-[20px] transition-colors ${
                                isEnrolled
                                  ? 'hover:bg-slate-100 cursor-pointer'
                                  : 'opacity-60 cursor-not-allowed'
                              }`}
                              onClick={(e) => !isEnrolled && e.preventDefault()}
                              onMouseEnter={() => isEnrolled && prefetchLesson(lesson.id)}
                              onFocus={() => isEnrolled && prefetchLesson(lesson.id)}
                            >
                              {/* Completion indicator */}
                              {lessonCompleted ? (
                                <div className="w-6 h-6 rounded-xl bg-emerald-500 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                                  <CheckCircle2 className="w-5 h-5 text-white" />
                                </div>
                              ) : isEnrolled ? (
                                <div className="w-6 h-6 rounded-xl border-2 border-slate-200 flex-shrink-0" />
                              ) : (
                                <div className="w-6 h-6 rounded-xl border-2 border-slate-200 flex items-center justify-center flex-shrink-0">
                                  <Lock className="w-3 h-3 text-slate-400" />
                                </div>
                              )}

                              {/* Lesson info */}
                              <div className="flex-1">
                                <div className="text-base font-normal text-slate-900">{lesson.title}</div>
                                <div className="text-sm text-slate-500">
                                  {lesson.duration_seconds ? `${Math.ceil(lesson.duration_seconds / 60)} min` : ''}
                                  {lessonProgress > 0 && !lessonCompleted && ` • ${Math.round(lessonProgress)}% watched`}
                                </div>
                              </div>

                              {/* Play button */}
                              <div className="w-10 h-10 rounded-[20px] bg-[#4669ed] flex items-center justify-center flex-shrink-0 text-white">
                                {getLessonIcon(lesson.lesson_type)}
                              </div>
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  // Flat lesson list
                  course.lessons.map((lesson) => {
                    const lessonCompleted = lesson.progress?.completed_at !== null && lesson.progress?.completed_at !== undefined
                    const lessonProgress = lesson.progress?.percent_watched || 0

                    return (
                      <Link
                        key={lesson.id}
                        to={isEnrolled ? `/genius/lesson/${lesson.id}` : '#'}
                        className={`flex items-center gap-4 p-4 rounded-[20px] transition-colors ${
                          isEnrolled
                            ? 'hover:bg-slate-100 cursor-pointer'
                            : 'opacity-60 cursor-not-allowed'
                        }`}
                        onClick={(e) => !isEnrolled && e.preventDefault()}
                        onMouseEnter={() => isEnrolled && prefetchLesson(lesson.id)}
                        onFocus={() => isEnrolled && prefetchLesson(lesson.id)}
                      >
                        {/* Completion indicator */}
                        {lessonCompleted ? (
                          <div className="w-6 h-6 rounded-xl bg-emerald-500 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                            <CheckCircle2 className="w-5 h-5 text-white" />
                          </div>
                        ) : isEnrolled ? (
                          <div className="w-6 h-6 rounded-xl border-2 border-slate-200 flex-shrink-0" />
                        ) : (
                          <div className="w-6 h-6 rounded-xl border-2 border-slate-200 flex items-center justify-center flex-shrink-0">
                            <Lock className="w-3 h-3 text-slate-400" />
                          </div>
                        )}

                        {/* Lesson info */}
                        <div className="flex-1">
                          <div className="text-base font-normal text-slate-900">{lesson.title}</div>
                          <div className="text-sm text-slate-500">
                            {lesson.duration_seconds ? `${Math.ceil(lesson.duration_seconds / 60)} min` : ''}
                            {lessonProgress > 0 && !lessonCompleted && ` • ${Math.round(lessonProgress)}% watched`}
                          </div>
                        </div>

                        {/* Play button */}
                        <div className="w-10 h-10 rounded-[20px] bg-[#4669ed] flex items-center justify-center flex-shrink-0 text-white">
                          {getLessonIcon(lesson.lesson_type)}
                        </div>
                      </Link>
                    )
                  })
                )}
              </div>
              </div>
            </SlideIn>
          </div>

          {/* Sidebar */}
          <StaggerContainer staggerDelay={0.15} delayChildren={0.3} className="w-[320px] flex-shrink-0 space-y-5">
            {/* Your Progress Card */}
            <StaggerItem>
              <div className="bg-white rounded-[40px] border border-slate-200 p-8">
              <h3 className="text-xl font-normal text-slate-900 tracking-tight mb-6">
                Your Progress
              </h3>

              <div className="space-y-5">
                {/* Videos Completed */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-3xl bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
                    <PlayCircle className="w-6 h-6 text-[#4669ed]" />
                  </div>
                  <div>
                    <div className="text-2xl font-normal text-slate-900 tracking-tight">
                      {completedLessons} / {totalLessons}
                    </div>
                    <div className="text-sm text-slate-500">Lessons Completed</div>
                  </div>
                </div>

                {/* Time Spent */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-3xl bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#4669ed]" />
                  </div>
                  <div>
                    <div className="text-2xl font-normal text-slate-900 tracking-tight">
                      {Math.ceil(watchedDurationSeconds / 60)} min
                    </div>
                    <div className="text-sm text-slate-500">Time Spent</div>
                  </div>
                </div>

                {/* Time Remaining */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-3xl bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
                    <Target className="w-6 h-6 text-[#4669ed]" />
                  </div>
                  <div>
                    <div className="text-2xl font-normal text-slate-900 tracking-tight">
                      ~{remainingMinutes} min
                    </div>
                    <div className="text-sm text-slate-500">Time Remaining</div>
                  </div>
                </div>
              </div>
              </div>
            </StaggerItem>

            {/* Action Card */}
            <StaggerItem>
              <div className="bg-white rounded-[40px] border border-slate-200 p-8">
                <h3 className="text-xl font-normal text-slate-900 tracking-tight text-center mb-3">
                {isCompleted ? 'Course Complete!' : isEnrolled ? 'Keep Going!' : 'Ready to Start?'}
              </h3>
              <p className="text-sm text-slate-500 text-center mb-6 leading-relaxed">
                {isCompleted
                  ? 'Congratulations on completing this course! Review the lessons anytime.'
                  : isEnrolled
                    ? `You're ${progressPercent}% through. Continue learning to complete your certification!`
                    : 'Enroll now to access all lessons and track your progress toward certification.'
                }
              </p>

              {isEnrolled ? (
                course.lessons.length > 0 && (
                  <Link
                    to={`/genius/lesson/${course.lessons.find(l => !l.progress?.completed_at)?.id || course.lessons[0].id}`}
                    className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-normal text-base"
                    style={{
                      background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                    }}
                  >
                    {isCompleted ? (
                      <>
                        <BookOpen className="w-5 h-5" />
                        Review Course
                      </>
                    ) : (
                      <>
                        <PlayCircle className="w-5 h-5" />
                        Continue Learning
                      </>
                    )}
                  </Link>
                )
              ) : (
                <button
                  onClick={() => enroll && enroll(course.id)}
                  disabled={isEnrolling || !enroll}
                  className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-normal text-base disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                  }}
                >
                  <GraduationCap className="w-5 h-5" />
                  {isEnrolling ? 'Enrolling...' : 'Enroll Now'}
                </button>
              )}
              </div>
            </StaggerItem>

            {/* Course Info Card */}
            {course.enrollment && (
              <StaggerItem>
                <div className="bg-white rounded-[40px] border border-slate-200 p-8">
                  <h3 className="text-xl font-normal text-slate-900 tracking-tight mb-4">
                    Enrollment Details
                  </h3>
                  <div className="space-y-3 text-sm">
                    {course.enrollment.enrolled_at && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Enrolled</span>
                        <span className="font-medium text-slate-900">
                          {new Date(course.enrollment.enrolled_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {course.enrollment.completed_at && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Completed</span>
                        <span className="font-medium text-slate-900">
                          {new Date(course.enrollment.completed_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status</span>
                      <span className={`font-medium ${isCompleted ? 'text-emerald-600' : 'text-[#4669ed]'}`}>
                        {isCompleted ? 'Completed' : 'In Progress'}
                      </span>
                    </div>
                  </div>
                </div>
              </StaggerItem>
            )}
          </StaggerContainer>
        </div>
      </div>
    </div>
  )
}
